import { useContext, useRef, useState, useEffect } from "react";
import Header from "../../Components/Header";
import { useNavigate } from "react-router-dom";
import PortalContext from "../PortalContext";
import { useGetTranslation } from "../../Components/Translate";
import { sendPostRequest, sendGetRequest } from "../../Client/index.js";
import ClipLoader from "react-spinners/ClipLoader";
import SkuRow from "./SkuRow";
import ServiceRow from "./ServiceRow";
import SkuRowMatched from "../Step-3/SkuRow.js";

export default function Quick() {
  const { portal } = useContext(PortalContext);
  const getTranslation = useGetTranslation();
  const navigate = useNavigate();

  const quickData = useRef({});
  const orderNumberRef = useRef(null);
  const emailRef = useRef(null);

  const [loading1, setLoading1] = useState(false);

  const [matched, setMatched] = useState(false);
  const [country, setCountry] = useState("");
  const [refInitialData, setRefInitialData] = useState(null);

  const [showAddress, setShowAddress] = useState(false);

  const [countyValue, setCountyValue] = useState("");

  const [emailVal, setEmailVal] = useState("");
  const [nameVal, setNameVal] = useState("");
  const [postcodeVal, setPostcodeVal] = useState("");
  const [address1Val, setAddress1Val] = useState("");
  const [townVal, setTownVal] = useState("");
  const [address2Val, setAddress2Val] = useState("");
  const [countyTextVal, setCountyTextVal] = useState("");

  const [loadingServices, setLoadingServices] = useState(false);
  const [skipItems, setSkipItems] = useState(false);

  const nameRef = useRef(null);
  const address1Ref = useRef(null);
  const address2Ref = useRef(null);
  const townRef = useRef(null);
  const countyRef = useRef(null);
  const postcodeRef = useRef(null);

  const countyDropdown = !!(
    portal?.data?.states && portal?.data?.states[country]
  );

  const defaultEmail = portal?.data?.settings?.defaultEmail?.trim() || null;

  const handleSubmit1 = async (e) => {
    e.preventDefault();
    setLoading1(true);

    // Reset previous search state
    setShowAddress(false);
    setMatched(false);
    setRefInitialData(null);
    setCountry("");
    setCountyValue("");
    setNameVal("");
    setPostcodeVal("");
    setAddress1Val("");
    setTownVal("");
    setAddress2Val("");
    setCountyTextVal("");
    setEmailVal("");

    // reset SKU rows
    setReturns(["Return 1"]);
    setInputs({ items: makeItems(1) });
    setSkipItems(false);

    // Reset SERVICES state
    setServicesData([]);
    setSelectedServiceId(null);
    setCreatingLabel(false);
    setLoadingServices(false);

    try {
      const orderNumber = orderNumberRef.current.value.trim();
      quickData.current = { orderNumber };

      const res = await sendPostRequest("api/v2-label-portal-lookup.php", {
        orderNumber,
        quick: true,
      });


      const isMatched =
        res?.match === true || res?.match === "true" || res?.match === 1;

      setMatched(isMatched);
      setRefInitialData(res || {});

      const iso =
        res?.Customer_country_iso ||
        res?.Customer_country_ISO ||
        res?.CountryIso ||
        res?.countryIso ||
        res?.["Customer_country ISO"] ||
        "";

      const isoUpper = (iso || "").toString().toUpperCase();
      setCountry(isoUpper);

      // preselect county if possible
      const countyFromApi = (res?.Customer_county || "").toString();
      let prefillCounty = "";
      const map = portal?.data?.states?.[isoUpper];
      if (map && countyFromApi) {
        const norm = (s) => s?.toString().trim().toLowerCase();
        const target = norm(countyFromApi);
        for (const [name, code] of Object.entries(map)) {
          if (norm(name) === target || norm(code) === target) {
            prefillCounty = name;
            break;
          }
        }
      }

      setCountyValue(prefillCounty);

      setShowAddress(true);

      if (isMatched && res) {
        setNameVal(res.Customer_name || "");
        setPostcodeVal(res.Customer_postcode || "");
        setAddress1Val(res["Customer_address 1"] || "");
        setTownVal(res.Customer_town || "");
        setEmailVal(res.Order_email || "");

        setAddress2Val(res["Customer_address 2"] || "");
        setCountyValue(prefillCounty);
        setCountyTextVal(res.Customer_county || "");
      }

      if (isMatched && Array.isArray(res?.SKU)) {
        const items = res.SKU.map((sku, i) => ({
          index: i,
          name: sku.SKU_name || sku.SKU_code || `SKU ${i + 1}`, // prefill name so validation passes
          quantity: 0,
          reason: "",
          action: "",
          selected: false,
        }));

        setReturns(items.map((_, i) => `Return ${i + 1}`)); // just to keep lengths aligned
        setInputs({ items });
      }
    } catch (err) {
      console.error("Error submitting:", err);
      setShowAddress(true);
    } finally {
      setLoading1(false);
    }
  };

  const [servicesData, setServicesData] = useState([]);

  const fetchServices = async () => {
    if (!skipItems && !allSkusValid) return;
    try {
      setLoadingServices(true);

      const reasonsArray = skipItems
        ? []
        : inputs.items
            .filter((it) => it.selected)
            .map((it) => it.reason)
            .filter(Boolean);

      const reasonsString = reasonsArray.join(",");

      const response = await sendGetRequest(
        `api/v2-label-portal-services.php` +
          `?country=${encodeURIComponent(country || "")}` +
          `&voucher=` +
          `&weight=` +
          `&reason=${encodeURIComponent(reasonsString)}`
      );

      const filtered = Array.isArray(response)
        ? response.filter((s) => s.serviceProvider !== 53)
        : response;

      setServicesData(filtered);
    } catch (err) {
      console.error("Error fetching services:", err);
    } finally {
      setLoadingServices(false);
    }
  };

  // logic for sku rows
  const initialReturns = ["Return 1"];

  const [returns, setReturns] = useState(initialReturns);

  const makeItems = (count) =>
    Array.from({ length: count }, (_, index) => ({
      index,
      name: "",
      quantity: 1,
      reason: "",
      action: "",
      selected: true,
    }));

  const [inputs, setInputs] = useState(() => ({
    items: portal?.step3manual?.items?.length
      ? portal.step3manual.items.map((it, i) => ({ index: i, ...it }))
      : makeItems(initialReturns.length),
  }));

  const setItem = (index, patch) => {
    setInputs((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], ...patch };
      return { ...prev, items };
    });
  };

  const deleteReturn = (label) => {
    setReturns((prev) => {
      const idx = prev.indexOf(label);
      if (idx === -1) return prev;

      setInputs((prevInputs) => {
        const items = prevInputs.items
          .filter((_, i) => i !== idx)
          .map((it, i) => ({ ...it, index: i }));
        return { ...prevInputs, items };
      });

      return prev.filter((_, i) => i !== idx);
    });
  };

  const addReturn = () => {
    setReturns((r) => [...r, `Return ${r.length + 1}`]);
    setInputs((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          index: prev.items.length,
          name: "",
          quantity: 1,
          reason: "",
          action: "",
          selected: true,
        },
      ],
    }));
  };
  // end of logic for sku rows

  // services logic
  const [selectedServiceId, setSelectedServiceId] = useState(null);

  const handleToggleService = (id) => {
    setSelectedServiceId((prev) => (prev === id ? null : id));
  };
  // end of services logic

  // label logic
  const [creatingLabel, setCreatingLabel] = useState(false);

  const handleGetLabel = async () => {
    try {
      setCreatingLabel(true);

      const selectedService = servicesData.find(
        (s) => s.serviceID === selectedServiceId
      );

      if (!selectedService) {
        alert("Please select a service first.");
        return;
      }

      const payload = buildPayload(selectedService);

      const res = await sendPostRequest(
        "api/v2-label-portal-create-order.php",
        payload
      );

      if (res?.status === "success" && res?.id) {
        navigate(`/order-success?s=${res.id}`, { replace: true });
      } else if (res?.status === "error" && res?.reason) {
        alert(res.reason);
      } else if (res?.status === "stripe" && res?.url) {
        window.location.href = res.url;
      } else {
        alert("Something went wrong creating the return label.");
      }
    } catch (err) {
      console.error("Error creating order:", err);
      alert("Unable to create the return label. Please try again.");
    } finally {
      setCreatingLabel(false);
    }
  };

  // end of label logic

  const canShowSku = !!(
    country &&
    nameVal.trim() &&
    postcodeVal.trim() &&
    address1Val.trim() &&
    townVal.trim()
  );

  const selectedItems = inputs.items.filter((it) => it.selected);

  const allSkusValid =
    selectedItems.length > 0 &&
    selectedItems.every((it) => {
      const hasName = (it.name || "").trim().length > 0;
      const qty = Number(it.quantity);
      const hasQty = Number.isFinite(qty) && qty > 0;
      const hasReason = (it.reason || "").trim().length > 0;
      const hasAction = (it.action || "").trim().length > 0;
      return hasName && hasQty && hasReason && hasAction;
    });

  const clearServices = () => {
    if (servicesData.length || selectedServiceId) {
      setServicesData([]);
      setSelectedServiceId(null);
    }
  };

  // Clear if ANY address input changes
  useEffect(() => {
    clearServices();
  }, [
    matched,
    country,
    nameVal,
    address1Val,
    address2Val,
    townVal,
    postcodeVal,
    countyValue,
    countyTextVal,
  ]);

  // getting the data in the correct structure to create the order
  const buildPayload = (service) => {
    const selectedItemsCleaned =
      skipItems
        ? []
        : inputs.items
            .filter((it) => it.selected && Number(it.quantity) > 0)
            .map((it) => ({
              ...it,
              quantity: Number(it.quantity),
            }));

    const step3 = {
      items: selectedItemsCleaned,
    };

    const step2 = {
      name: nameVal,
      address1: address1Val,
      address2: address2Val,
      town: townVal,
      county: countyDropdown ? countyValue : countyTextVal,
      postcode: postcodeVal,
      country,
    };

    return {
      ...portal,
      step1: {
        orderNumber: orderNumberRef.current.value,
        email:
          defaultEmail ||
          (emailRef.current ? emailRef.current.value : emailVal),
        postcode: postcodeRef.current.value,
        quick: true,
      },
      step2,
      step3,
      step4: service,
    };
  };

  return (
    <>
      <Header />

      <section className="page3-slider">
        <div className="container mt-4 mb-3">
          <h2 className="mb-4">Quick Label</h2>

          <form onSubmit={handleSubmit1}>
            <div className="row align-items-center mb-3">
              <div className="col-lg-4 col-md-8 col-12">
                <input
                  type="text"
                  placeholder="Order number"
                  name="orderNumber"
                  id="orderNumber"
                  ref={orderNumberRef}
                  defaultValue={quickData.current.orderNumber}
                  required
                  className="form-control"
                />
              </div>

              <div className="col-lg-4 col-md-4 col-12 text-md-start">
                <button
                  type="submit"
                  disabled={loading1}
                  style={{
                    backgroundColor:
                      portal?.data?.settings?.colours?.primary || "#0d6efd",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    minHeight: 40,
                    minWidth: 160,
                    border: "none",
                    color: "#fff",
                    borderRadius: 6,
                    opacity: loading1 ? 0.7 : 1,
                    cursor: loading1 ? "not-allowed" : "pointer",
                    fontWeight: 600,
                  }}
                >
                  {loading1 ? <ClipLoader size={20} color="#fff" /> : "Search"}
                </button>
              </div>
            </div>
          </form>

          {showAddress && (
            <div className="mt-3 mb-8">
              <form
                key={quickData.current.orderNumber}
                className="align-items-center "
              >
                {!defaultEmail && (
                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <input
                        type="email"
                        placeholder="Email"
                        name="email"
                        ref={emailRef}
                        defaultValue={
                          matched && refInitialData?.Order_email
                            ? refInitialData.Order_email
                            : ""
                        }
                        onChange={(e) => setEmailVal(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <div className="row ">
                  <div className="col-sm-4">
                    <select
                      onChange={(e) => setCountry(e.target.value)}
                      id="country"
                      name="country"
                      required
                      style={{
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        backgroundColor: "white",
                        color: "black",
                        paddingRight: "2.5rem",
                        backgroundImage:
                          "url(\"data:image/svg+xml;utf8,<svg fill='%23333' height='12' viewBox='0 0 20 20' width='12' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>\")",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 0.75rem center",
                        backgroundSize: "1rem",
                        border: "1px solid #ccc",
                        borderRadius: "5px",
                      }}
                      value={country || ""}
                    >
                      {matched === false ? (
                        <option value="" disabled>
                          Select a Country
                        </option>
                      ) : null}
                      {(portal?.data?.settings?.countries || []).map((c) => (
                        <option value={c.iso} key={c.iso}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row mt-2 mobile-no-wrap">
                  <div className="col-sm-4">
                    <input
                      required
                      type="text"
                      placeholder={"Your name"}
                      name="name"
                      ref={nameRef}
                      defaultValue={
                        matched && refInitialData?.Customer_name
                          ? refInitialData.Customer_name
                          : ""
                      }
                      onChange={(e) => setNameVal(e.target.value)}
                    />
                  </div>

                  <div className="col-sm-4">
                    <input
                      required
                      type="text"
                      placeholder={"Address line 1"}
                      name="address1"
                      ref={address1Ref}
                      defaultValue={
                        (matched &&
                          (refInitialData?.["Customer_address 1"] ||
                            refInitialData?.Customer_address1 ||
                            refInitialData?.Customer_address_1)) ||
                        ""
                      }
                      onChange={(e) => setAddress1Val(e.target.value)}
                    />
                  </div>
                </div>

                <div className="row mt-2 mobile-no-wrap">
                  <div className="col-sm-4">
                    <input
                      type="text"
                      placeholder={"Address line 2"}
                      name="address2"
                      ref={address2Ref}
                      defaultValue={
                        (matched &&
                          (refInitialData?.["Customer_address 2"] ||
                            refInitialData?.Customer_address2 ||
                            refInitialData?.Customer_address_2)) ||
                        ""
                      }
                      onChange={(e) => setAddress2Val(e.target.value)}
                    />
                  </div>

                  <div className="col-sm-4">
                    <input
                      required
                      type="text"
                      placeholder={"Town"}
                      name="town"
                      ref={townRef}
                      defaultValue={
                        matched && refInitialData?.Customer_town
                          ? refInitialData.Customer_town
                          : ""
                      }
                      onChange={(e) => setTownVal(e.target.value)}
                    />
                  </div>
                </div>

                <div className="row mt-2 mobile-no-wrap">
                  {countyDropdown ? (
                    <div className="col-sm-4">
                      <select
                        id="county"
                        name="county"
                        required
                        value={countyValue}
                        onChange={(e) => setCountyValue(e.target.value)}
                        style={{ backgroundColor: "white", color: "black" }}
                      >
                        <option value="">Please select a County / State</option>
                        {Object.entries(
                          portal?.data?.states?.[country] || {}
                        ).map(([name, code]) => (
                          <option key={name} value={name}>
                            {name} - {code}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="col-sm-4">
                      <input
                        type="text"
                        placeholder={"County / State"}
                        name="county"
                        ref={countyRef}
                        defaultValue={
                          countyValue ||
                          (matched && refInitialData?.Customer_county
                            ? refInitialData.Customer_county
                            : "")
                        }
                        onChange={(e) => setCountyTextVal(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="col-sm-4">
                    <input
                      required
                      type="text"
                      placeholder={"Postcode / Zip"}
                      name="postcode"
                      ref={postcodeRef}
                      onInput={(e) =>
                        (e.target.value = e.target.value.toUpperCase())
                      }
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      defaultValue={
                        matched && refInitialData?.Customer_postcode
                          ? refInitialData.Customer_postcode
                          : ""
                      }
                      onChange={(e) => setPostcodeVal(e.target.value)}
                    />
                  </div>
                </div>
              </form>
            </div>
          )}

          {showAddress && canShowSku && (
            <div className="mt-3">
              {!skipItems &&
                (matched ? refInitialData?.SKU ?? [] : returns)?.map(
                  (row, index) => {
                    const RowComponent = matched ? SkuRowMatched : SkuRow;
                    return (
                      <RowComponent
                        key={index}
                        index={index} // <-- pass index
                        item={row} // when matched, row is the SKU object
                        deleteReturn={deleteReturn}
                        addReturn={addReturn}
                        itemState={inputs.items[index]}
                        setItem={setItem}
                      />
                    );
                  }
                )}

              <div
                className="mt-3 mb-2 d-flex align-items-center gap-2"
                style={{ justifyContent: "flex-start" }}
              >
                <input
                  type="checkbox"
                  id="skip-items"
                  checked={skipItems}
                  onChange={(e) => setSkipItems(e.target.checked)}
                  style={{
                    width: 16,
                    height: 16,
                    margin: 0,
                    flexShrink: 0,
                    cursor: "pointer",
                  }}
                />
                <label
                  htmlFor="skip-items"
                  style={{
                    marginBottom: 0,
                    marginLeft: 10,
                    cursor: "pointer",
                    fontSize: "0.95rem",
                    userSelect: "none",
                  }}
                >
                  <strong>Unknown Items:</strong> tick only if generating a label to go in your outbound orders. This will associate all items ordered with the return label
                </label>
              </div>

              <div className="mt-4 d-flex justify-content-start">
                <button
                  type="button"
                  onClick={fetchServices}
                  disabled={
                    (!allSkusValid && !skipItems) || loadingServices
                  }
                  style={{
                    backgroundColor:
                      (!allSkusValid && !skipItems) || loadingServices
                        ? "#95979A"
                        : "#13a23eff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    minHeight: 40,
                    minWidth: 180,
                    border: "none",
                    color: "#fff",
                    borderRadius: 6,
                    fontWeight: 600,
                    cursor:
                      (!allSkusValid && !skipItems) || loadingServices
                        ? "not-allowed"
                        : "pointer",
                    opacity: loadingServices ? 0.8 : 1,
                    transition: "background-color 0.2s ease",
                  }}
                  title={
                    !allSkusValid && !skipItems
                      ? "Enter product name, quantity (>0), reason, and action for each item, or tick 'No items to return'"
                      : ""
                  }
                >
                  {loadingServices ? (
                    <ClipLoader size={20} color="#fff" />
                  ) : (
                    "Get services"
                  )}
                </button>
              </div>
            </div>
          )}

          {servicesData?.length > 0 && (
            <div className="mt-5">
              <h4 className="mb-3">Available Services</h4>

              <div className="row g-3">
                {servicesData.map((service) => (
                  <div
                    className="col-12 col-md-6 col-lg-4 mb-2"
                    key={service.serviceID}
                  >
                    <ServiceRow
                      key={service.serviceID}
                      service={service}
                      selected={selectedServiceId === service.serviceID}
                      onToggle={handleToggleService}
                    />
                  </div>
                ))}
              </div>

              {selectedServiceId && (
                <div className="mt-5 mb-12">
                  <button
                    type="button"
                    onClick={handleGetLabel}
                    disabled={creatingLabel}
                    style={{
                      backgroundColor: "#13a23eff",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      minHeight: 40,
                      minWidth: 180,
                      border: "none",
                      color: "#fff",
                      borderRadius: 6,
                      fontWeight: 600,
                      cursor: creatingLabel ? "not-allowed" : "pointer",
                      opacity: creatingLabel ? 0.7 : 1,
                    }}
                  >
                    {creatingLabel ? (
                      <ClipLoader size={20} color="#fff" />
                    ) : (
                      "Create return"
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
